import axios from 'axios';

import { AudienceByWorkspaceResponse } from '@/models/AudienceByWorkspace';

import { backendRequestHeaders, getBackendBaseUrl } from './backendAuth';
import { normalizeAudienceResponse } from './workspaces/normalizeAudienceResponse';

/** GET /workspaces/{id}/users/members — same shape as activity-logs: { members, total, limit, offset } */
export interface BackendWorkspaceMembersResponse {
  members?: unknown[];
  total?: number;
}

/** Same max page size as GET /activity-logs (workspace list). */
export const WORKSPACE_MEMBERS_MAX_LIMIT = 500;

export type FetchWorkspaceMembersOptions = {
  /** `paged`: one page via limit/offset. `all`: single request with limit 500 (same cap as activity logs). */
  mode: 'all' | 'paged';
  pageIndex?: number;
  pageSize?: number;
  search?: string;
  /** Server sort when mode is paged: email | riskScore | engagement */
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
};

/**
 * Loads workspace audience (users) from the backend — GET /workspaces/{id}/users/members
 * (always sends limit/offset, matching activity logs).
 */
export async function fetchWorkspaceMembersFromBackend(
  workspaceId: string,
  sessionJwt?: string | null,
  options?: FetchWorkspaceMembersOptions,
): Promise<AudienceByWorkspaceResponse> {
  const base = getBackendBaseUrl();
  const headers = backendRequestHeaders(sessionJwt);
  if (!headers.Authorization) {
    throw new Error('Missing Bearer token: set REACT_APP_DEV_JWT (see web/env/.env.local.user) or sign in with a token.');
  }

  const url = `${base}/workspaces/${workspaceId}/users/members`;
  const mode = options?.mode ?? 'paged';
  const params: Record<string, string | number> = {};

  if (mode === 'all') {
    params.limit = WORKSPACE_MEMBERS_MAX_LIMIT;
    params.offset = 0;
  } else {
    const pageSize = Math.min(Math.max(options?.pageSize ?? 10, 1), WORKSPACE_MEMBERS_MAX_LIMIT);
    const pageIndex = options?.pageIndex ?? 0;
    params.limit = pageSize;
    params.offset = pageIndex * pageSize;
    const s = options?.search?.trim();
    if (s) params.search = s;
    if (options?.sortBy) params.sortBy = options.sortBy;
    if (options?.sortDir) params.sortDir = options.sortDir;
  }

  const { data } = await axios.get<BackendWorkspaceMembersResponse | unknown>(url, { headers, params });
  return normalizeAudienceResponse(data);
}
